/* eslint-disable @next/next/no-img-element */
import { useState, useEffect } from "react";
import { useRecoilState } from "recoil";
import clsx from "clsx";
import axios from "axios";

import styles from "../../styles/tinymce.module.css";

import { engageState } from "../../atoms/platform";
import { getItemNumberOnly, getGroupForTitleId } from "../../helpers/text";
import { getNameByGroupId } from "../../helpers/arrays";
import { apiGetTopicPrintClean } from "../../lib/api/topics";
import { textToHtmlTinyMCE } from "../../helpers/converter";

import {
  motion,
  //   animate,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { Dialog, ModalOverlay, Modal, Button } from "react-aria-components";
import {
  IoAttachOutline,
  IoDocumentTextOutline,
  IoDownload,
} from "react-icons/io5";
import { AiOutlinePrinter, AiOutlineClockCircle } from "react-icons/ai";

import { XIcon } from "@heroicons/react/outline";

import MobileReader from "../RichText/MobileReader";
import EditorReadOnly from "../RichText/EditorReadOnly";
import CustomFieldPrint from "../Actions/Dark/CustomFieldPrint";
import PDFViewer from "../Actions/PDFViewer";
import EditorHTML from "../RichText/EditorHTML";
import EditorHTMLSmall from "../RichText/EditorHTMLSmall";
import TallyResults from "./TallyResults";
import Loading from "../Shared/Loading";
import FileDrop from "../Shared/DropDown/FileDrop";
import TinyiFrameMobile from "../TinyMce/TinyiFrameMobile";

// Wrap React Aria modal components so they support framer-motion values.
const MotionModal = motion(Modal);
const MotionModalOverlay = motion(ModalOverlay);

// const inertiaTransition = {
//   type: "inertia",
//   bounceStiffness: 300,
//   bounceDamping: 40,
//   timeConstant: 300,
// };

const staticTransition = {
  duration: 0.5,
  ease: [0.32, 0.72, 0, 1],
};

const SHEET_MARGIN = 17;
// const SHEET_RADIUS = 12;

function MinutesTopicInfoMobile({ visible, topic, enablePri, client }) {
  const [platform, setPlatform] = useRecoilState(engageState);
  const [section, setSection] = useState(1);
  const [ranked, setRanked] = useState();
  const [localTopic, setLocalTopic] = useState();
  const [resources, setResources] = useState([]);
  const [mobileFile, setMobileFile] = useState();
  const [loading, setLoading] = useState(false);

  let h = window.innerHeight - SHEET_MARGIN;
  let y = useMotionValue(h);
  let bgOpacity = useTransform(y, [0, h], [0.4, 0]);
  let bg = useMotionTemplate`rgba(0, 0, 0, ${bgOpacity})`;

  useEffect(() => {
    async function fetchTopic() {
      setLoading(true);
      const result = await apiGetTopicPrintClean(topic);
      if (result) {
        setLocalTopic(result);
        if (!platform) {
          setPlatform(result.platform);
        }
        if (result.attachments?.length > 0) {
          const filteredArray = result.attachments.filter(
            (obj) =>
              obj.public === true && obj.fileType?.toLowerCase() === "pdf",
          );
          setResources(filteredArray);
          setMobileFile(filteredArray[0]);
        }
        if (result.rankedVoting) {
          setRanked(JSON.parse(result.rankedVoting));
        }
      }
      setLoading(false);
    }
    if (topic) {
      fetchTopic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic]);

  const handleFileChange = (value) => {
    setMobileFile(value);
  };

  const handleDownload = () => {
    axios({
      url: mobileFile?.fileUrl,
      method: "GET",
      responseType: "blob",
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${mobileFile.fileName}.pdf`); // Specify the desired filename and extension
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error("Error downloading the file:", error);
      });
  };

  return (
    <MotionModalOverlay
      // Force the modal to be open when AnimatePresence renders it.
      isOpen
      onOpenChange={visible}
      className="fixed inset-0 z-50"
      style={{ backgroundColor: bg }}
    >
      <MotionModal
        className="bg-white absolute w-full bottom-0 rounded-t-xl shadow-lg"
        initial={{ y: h }}
        animate={{ y: 0 }}
        exit={{ y: h }}
        transition={staticTransition}
        style={{
          y,
          top: SHEET_MARGIN,
          // Extra padding at the bottom to account for rubber band scrolling.
          paddingBottom: window.screen.height,
        }}
        // drag="y"
        // dragConstraints={{ top: 0 }}
        // onDragEnd={(e, { offset, velocity }) => {
        //   if (offset.y > window.innerHeight * 0.75 || velocity.y > 10) {
        //     visible(false);
        //   } else {
        //     animate(y, 0, { ...inertiaTransition, min: 0, max: 0 });
        //   }
        // }}
      >
        {/* drag affordance */}
        {/* <div className="mx-auto w-12 mt-2 h-1.5 rounded-full bg-gray-400" /> */}
        <Dialog className="px-4 pb-4 outline-none">
          {/* Menu Selection */}
          <div className="pt-4 border-b border-zinc-700">
            <div className="mt-4 w-full grid grid-cols-4 2xl:grid-cols-7">
              <div
                className={clsx(
                  section === 1
                    ? "text-blue-500 border-b-2 border-blue-500"
                    : "text-black",
                  "flex items-center justify-center transform duration-200 cursor-pointer hover:text-blue-500 pb-1 2xl:col-span-2",
                )}
                onClick={() => setSection(1)}
              >
                <IoDocumentTextOutline className="h-6 w-6" />
                <p className="text-sm hidden md:block">Details</p>
              </div>
              <div
                className={clsx(
                  section === 2
                    ? "text-blue-500 border-b-2 border-blue-500"
                    : "text-black",
                  "flex items-center justify-center transform duration-200 cursor-pointer hover:text-blue-500 pb-1 2xl:col-span-2",
                )}
                onClick={() => setSection(2)}
              >
                <IoAttachOutline className="h-6 w-6" />
                <p className="text-sm hidden md:block">Attachments</p>
              </div>
              <div
                className={clsx(
                  section === 3
                    ? "text-blue-500 border-b-2 border-blue-500"
                    : "text-black",
                  "flex items-center justify-center transform duration-200 cursor-pointer hover:text-blue-500 pb-1 2xl:col-span-2",
                )}
                onClick={() => setSection(3)}
              >
                <AiOutlineClockCircle className="h-6 w-6 mr-1.5" />
                <p className="text-sm hidden md:block">Minutes</p>
              </div>
              <div className="flex items-center justify-end space-x-4 2xl:space-x-3">
                <a
                  href={`${process.env.NEXT_PUBLIC_BASE_URL}/${client}/print/action/${topic}?print=true`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center transition duration-200"
                >
                  <AiOutlinePrinter className="h-5 w-5" />
                </a>
                <Button
                  className="text-blue-600 font-semibold outline-none rounded data-[pressed]:text-blue-700 data-[focus-visible]:ring md:hidden"
                  onPress={() => visible(false)}
                >
                  Done
                </Button>
                <button
                  type="button"
                  className="rounded-md text-black hover:text-delete-btn focus:outline-none cursor-pointer hover:rotate-180 transform duration-150 hidden md:block"
                  onClick={() => {
                    visible(false);
                  }}
                >
                  <span className="sr-only">Close panel</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
          {/* Details */}
          <div className="h-screen flex flex-col overflow-hidden">
            {section === 1 && platform && localTopic && (
              <>
                {localTopic.hasDetails ? (
                  <>
                    {localTopic.editorType === 1 ? (
                      <div className="grid grid-cols-7 md:grid-cols-6 mx-2 2xl:mx-6 overflow-y-auto pb-md">
                        <div className="relative col-span-7 md:col-span-2 min-h-[6em] overflow-hidden">
                          <img
                            className="absolute object-contain h-full w-full p-4"
                            src={platform.logo}
                            alt=""
                          />
                        </div>
                        <div className="col-span-7 md:col-span-4 border-b border-black mt-2">
                          {/* <div className="h-full w-full overflow-hidden grid grid-rows-2"> */}
                          <div className="flex justify-end items-end font-semibold md:text-xl">
                            <h3 className="">{platform.clientName}</h3>
                          </div>
                          <div className="flex justify-end items-center font-semibold text-sm 2xl:text-lg">
                            <h3 className="text-right">
                              {localTopic.department?.name}{" "}
                              {platform.memoCalled}
                            </h3>
                          </div>
                          {/* </div> */}
                        </div>
                        {platform?.fieldPreparedBy && (
                          <div className="col-span-7 md:col-span-6 w-full flex justify-end">
                            <p className="text-[10px] md:text-[12px]">
                              Prepared By: {localTopic.modifiedBy?.name}
                            </p>
                          </div>
                        )}
                        {/* Agenda Title */}
                        <div className="mt-4 col-span-7 md:col-span-6 border-b border-gray-800 text-[12px]">
                          <p className="font-semibold 2xl:text-lg">
                            {localTopic.title}
                          </p>
                          <p className="whitespace-pre-line">
                            {localTopic.topicBlurb}
                          </p>
                          {localTopic.subTopics?.length > 0 &&
                            localTopic.subTopics.map((sub, sIdx) => {
                              return (
                                <p key={`sub-${sIdx}`} className="ml-8 py-0.5">
                                  &#8212; {sub}
                                </p>
                              );
                            })}
                        </div>
                        {/* General Info */}
                        <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                          <div className="bg-gray-200 border-b border-gray-800 mb-1">
                            <p className="text-center font-medium">Meeting</p>
                          </div>
                          <div className="">
                            <p className="text-center">
                              {localTopic.meeting?.scheduleDate}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                          <div className="bg-gray-200 border-b border-gray-800 mb-1">
                            <p className="text-center font-medium">
                              Agenda Group
                            </p>
                          </div>
                          <div className="flex items-center justify-evenly">
                            <p className="">
                              {getNameByGroupId(
                                localTopic.agendaGroup.id,
                                localTopic.meeting?.groupSubheadings,
                                localTopic.agendaGroup.name,
                              )}
                            </p>
                            <p className="">
                              Item:{" "}
                              {localTopic.meeting && (
                                <>
                                  {getGroupForTitleId(
                                    localTopic.meeting.groupNumbering,
                                    localTopic.agendaGroup.id,
                                    localTopic.meeting.groupOrder,
                                    localTopic.meeting.childNumbering,
                                  )}
                                  {getItemNumberOnly(
                                    localTopic.meeting.allTopics,
                                    localTopic.meeting.itemNumbering,
                                    localTopic,
                                    localTopic.order - 1,
                                    localTopic.meeting.groupNumbering,
                                  )}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                        {localTopic.votedByBoard && (
                          <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">
                                Voted By Board
                              </p>
                            </div>
                            <div className="flex items-center justify-evenly">
                              <p className="">
                                {localTopic.votedByBoard?.name}
                              </p>
                            </div>
                          </div>
                        )}
                        {/* File and Presenter */}
                        {platform?.fieldReferenceFile && (
                          <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">
                                Reference File
                              </p>
                            </div>
                            <div>
                              <div className="min-h-[12px]">
                                <p className="text-center">
                                  {localTopic.referenceFile}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        {platform?.fieldPresentedBy && (
                          <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">
                                Presented By
                              </p>
                            </div>
                            <div className="">
                              <p className="text-center">
                                {localTopic.presentedBy}
                              </p>
                            </div>
                          </div>
                        )}
                        {/* Custom Fields */}
                        <CustomFieldPrint
                          client={client}
                          fields={localTopic.customFields}
                        />
                        {/* Community Goals */}
                        {platform?.enablePriorities && (
                          <div className="mt-2 col-span-7 md:col-span-6 border border-gray-800 text-[10px] md:text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">
                                Community Goals
                              </p>
                            </div>
                            <div className="px-3 py-2">
                              <div className="flex flex-wrap space-x-2">
                                {localTopic.priorities.map((item, index) => {
                                  if (item.checked) {
                                    return (
                                      <li
                                        key={index}
                                        className="flex mt-0.5 items-center pl-2"
                                      >
                                        <p className="font-medium text-[10px] md:text-[12px]">
                                          &#9745; {item.name}
                                        </p>
                                      </li>
                                    );
                                  } else return null;
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Details */}
                        <div className="flex-1 px-2 pb-[400px] md:hidden col-span-7 md:col-span-6 mt-6">
                          {localTopic && (
                            <MobileReader value={localTopic.details} />
                          )}
                        </div>
                        <div className="flex-1 px-2 pb-[400px] hidden md:block col-span-7 md:col-span-6">
                          {localTopic && (
                            <EditorReadOnly
                              value={localTopic.details}
                              docId={localTopic?.id}
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="overflow-y-auto pb-sm">
                        {localTopic.useBuiltInHeader && (
                          <div className="grid grid-cols-7 md:grid-cols-6 mx-2 2xl:mx-6">
                            <div className="relative col-span-7 md:col-span-2 min-h-[6em] overflow-hidden">
                              <img
                                className="absolute object-contain h-full w-full p-4"
                                src={platform.logo}
                                alt=""
                              />
                            </div>
                            <div className="col-span-7 md:col-span-4 border-b border-black mt-2">
                              {/* <div className="h-full w-full overflow-hidden grid grid-rows-2"> */}
                              <div className="flex justify-end items-end font-semibold md:text-xl">
                                <h3 className="">{platform.clientName}</h3>
                              </div>
                              <div className="flex justify-end items-center font-semibold text-sm 2xl:text-lg">
                                <h3 className="text-right">
                                  {localTopic.department?.name}{" "}
                                  {platform.memoCalled}
                                </h3>
                              </div>
                              {/* </div> */}
                            </div>
                            {platform?.fieldPreparedBy && (
                              <div className="col-span-7 md:col-span-6 w-full flex justify-end">
                                <p className="text-[10px] md:text-[12px]">
                                  Prepared By: {localTopic.modifiedBy?.name}
                                </p>
                              </div>
                            )}
                            {/* Agenda Title */}
                            <div className="mt-4 col-span-7 md:col-span-6 border-b border-gray-800 text-[12px]">
                              <p className="font-semibold 2xl:text-lg">
                                {localTopic.title}
                              </p>
                              <p className="whitespace-pre-line">
                                {localTopic.topicBlurb}
                              </p>
                              {localTopic.subTopics?.length > 0 &&
                                localTopic.subTopics.map((sub, sIdx) => {
                                  return (
                                    <p
                                      key={`sub-${sIdx}`}
                                      className="ml-8 py-0.5"
                                    >
                                      &#8212; {sub}
                                    </p>
                                  );
                                })}
                            </div>
                            {/* General Info */}
                            <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                              <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                <p className="text-center font-medium">
                                  Meeting
                                </p>
                              </div>
                              <div className="">
                                <p className="text-center">
                                  {localTopic.meeting.scheduleDate}
                                </p>
                              </div>
                            </div>
                            <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                              <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                <p className="text-center font-medium">
                                  Agenda Group
                                </p>
                              </div>
                              <div className="flex items-center justify-evenly">
                                <p className="">
                                  {getNameByGroupId(
                                    localTopic.agendaGroup.id,
                                    localTopic.meeting.groupSubheadings,
                                    localTopic.agendaGroup.name,
                                  )}
                                </p>
                                <p className="">
                                  Item:{" "}
                                  {getGroupForTitleId(
                                    localTopic.meeting.groupNumbering,
                                    localTopic.agendaGroup.id,
                                    localTopic.meeting.groupOrder,
                                    localTopic.meeting.childNumbering,
                                  )}
                                  {getItemNumberOnly(
                                    localTopic.meeting.allTopics,
                                    localTopic.meeting.itemNumbering,
                                    localTopic,
                                    localTopic.order - 1,
                                    localTopic.meeting.groupNumbering,
                                  )}
                                </p>
                              </div>
                            </div>
                            {localTopic.votedByBoard && (
                              <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                                <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                  <p className="text-center font-medium">
                                    Voted By Board
                                  </p>
                                </div>
                                <div className="flex items-center justify-evenly">
                                  <p className="">
                                    {localTopic.votedByBoard?.name}
                                  </p>
                                </div>
                              </div>
                            )}
                            {/* File and Presenter */}
                            {platform?.fieldReferenceFile && (
                              <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                                <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                  <p className="text-center font-medium">
                                    Reference File
                                  </p>
                                </div>
                                <div>
                                  <div className="min-h-[12px]">
                                    <p className="text-center">
                                      {localTopic.referenceFile}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                            {platform?.fieldPresentedBy && (
                              <div className="mt-2 col-span-7 md:col-span-3 border border-gray-800 pb-1 text-[12px]">
                                <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                  <p className="text-center font-medium">
                                    Presented By
                                  </p>
                                </div>
                                <div className="">
                                  <p className="text-center">
                                    {localTopic.presentedBy}
                                  </p>
                                </div>
                              </div>
                            )}
                            {/* Custom Fields */}
                            <CustomFieldPrint
                              client={client}
                              fields={localTopic.customFields}
                            />
                            {/* Community Goals */}
                            {platform?.enablePriorities && (
                              <div className="mt-2 col-span-7 md:col-span-6 border border-gray-800 text-[10px] md:text-[12px]">
                                <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                  <p className="text-center font-medium">
                                    Community Goals
                                  </p>
                                </div>
                                <div className="px-3 py-2">
                                  <div className="flex flex-wrap space-x-2">
                                    {localTopic.priorities.map(
                                      (item, index) => {
                                        if (item.checked) {
                                          return (
                                            <li
                                              key={index}
                                              className="flex mt-0.5 items-center pl-2"
                                            >
                                              <p className="font-medium text-[10px] md:text-[12px]">
                                                &#9745; {item.name}
                                              </p>
                                            </li>
                                          );
                                        } else return null;
                                      },
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <TinyiFrameMobile
                          data={localTopic.details}
                          frameId={localTopic.id}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <p className="mt-4 text-center font-semibold">
                    No Published Details
                  </p>
                )}
              </>
            )}
            {section === 2 && (
              <div className="flex-1 px-2 overflow-hidden">
                {resources?.length === 0 && (
                  <p className="text-center mt-2">No Attachments</p>
                )}
                {resources?.length > 0 && mobileFile && (
                  <div className="mt-2 flex items-center">
                    {mobileFile && (
                      <IoDownload
                        className="h-5 w-5 md:mt-1 md:h-8 md:w-8 text-blue-500 mr-2 hover:text-blue-300 transition duration-200 cursor-pointer"
                        onClick={() => handleDownload()}
                      />
                    )}
                    <FileDrop
                      array={resources}
                      value={mobileFile}
                      change={handleFileChange}
                      heading="Choose File"
                    />
                  </div>
                )}
                {mobileFile && (
                  <div className="h-screen pb-[175px] overflow-y-auto">
                    <PDFViewer urlFile={mobileFile} />
                  </div>
                )}
              </div>
            )}
            {section === 3 && (
              <div className="flex-1 px-4 2xl:px-6 pb-[400px] overflow-y-auto">
                <div className="mt-4">
                  {localTopic?.minutesInfo?.length > 0 ? (
                    <>
                      {localTopic.minutesEditor === 1 && (
                        <>
                          <div className="hidden md:block">
                            <EditorHTML value={localTopic.minutesInfo} />
                          </div>
                          <div className="md:hidden">
                            <EditorHTMLSmall value={localTopic.minutesInfo} />
                          </div>
                        </>
                      )}
                      {localTopic.minutesEditor === 2 && (
                        <div className={`mb-4 ${styles.tinyContainer}`}>
                          {textToHtmlTinyMCE(localTopic.minutesInfo)}
                        </div>
                      )}
                    </>
                  ) : (
                    <p>No Minutes Details</p>
                  )}
                </div>
                {/* Motions */}
                {localTopic.motions?.length > 0 && localTopic.hasDetails && (
                  <div className="sm:space-y-2 flex flex-col mb-4 mt-4">
                    <p className="font-semibold">Motions</p>
                    <div>
                      {localTopic.motions.map((motion, index) => (
                        <div className="flex flex-col space-y-1" key={index}>
                          <p className="font-medium text-base">
                            {index + 1}. Motion Type: {motion.motionType?.name}
                          </p>
                          <p className="pl-4 font-medium">
                            Motion By: {motion.motionedBy.name}
                          </p>
                          <p className="pl-4 font-medium">
                            Seconded By: {motion.secondedBy?.name}
                          </p>
                          {motion.wording?.length > 0 && (
                            <p className="pl-4 leading-none italic border-t border-zinc-700 pt-1">
                              {motion.wording}
                            </p>
                          )}
                          <p className="pl-4">
                            Vote Result:{" "}
                            {motion.votingResult === true ? "Passed" : "Failed"}
                          </p>
                          {localTopic.votingOption !== "RANKED" ? (
                            <>
                              {/* <p className="pl-4">Votes:</p> */}
                              <div className="grid grid-cols-1 gap-1 md:w-1/2">
                                {!motion.secretBallot &&
                                  motion.votes.map((vote, index) => {
                                    if (vote.counts !== "recuse") {
                                      return (
                                        <div
                                          key={vote._id}
                                          className={clsx(
                                            vote.counts === "positive"
                                              ? "bg-green-700"
                                              : "",
                                            vote.counts === "negative"
                                              ? "bg-red-700"
                                              : "",
                                            vote.counts === "no-vote"
                                              ? "bg-zinc-700"
                                              : "",
                                            vote.counts === "recuse"
                                              ? "bg-zinc-700"
                                              : "",
                                            vote.counts === "absent"
                                              ? "bg-zinc-700"
                                              : "",
                                            "relative rounded-lg pr-2 flex items-center space-x-2",
                                          )}
                                        >
                                          <div className="flex-shrink-0">
                                            <img
                                              className="h-10 w-10 rounded-l-md"
                                              src={vote.member.photoUrl}
                                              alt=""
                                            />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="focus:outline-none">
                                              <span className="absolute inset-0" />
                                              <p className="text-xs lg:text-sm 2xl:text-base text-zinc-200">
                                                {vote.member.name}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return null;
                                    }
                                  })}
                              </div>

                              {motion.secretBallot && (
                                <TallyResults votes={motion.votes} />
                              )}
                            </>
                          ) : (
                            <>
                              <p className="pl-4">
                                Ranked Vote with {ranked?.openings} spots
                                filled:
                              </p>
                              {ranked?.approved.map((vote, index) => (
                                <p
                                  className="pl-8 font-medium py-0"
                                  key={index * 300}
                                >
                                  {vote}
                                </p>
                              ))}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {loading && <Loading />}
          </div>
        </Dialog>
      </MotionModal>
    </MotionModalOverlay>
  );
}

export default MinutesTopicInfoMobile;
